(function () {
  'use strict';

  /* ============================================================
     Aurum Brew — page transition veil
     assets/aurum-veil.js

     Raises the veil when the customer commits to going somewhere, and
     leaves it up until the browser replaces the document.

     The whole file is a filter. Deciding to show a veil is one line; the
     work is deciding when NOT to, because every false positive is a
     customer watching a curtain come down over a page that is not going to
     change. Anything not clearly an ordinary same-tab navigation is left
     alone.
     ============================================================ */

  var VEIL_ID = 'aurum-veil';
  var SAFETY_MS = 4000;

  /* How long to hold after the emblem has finished settling, so the glow and
     ring are visibly in motion at the moment the page turns over. Tuned
     against the keyframes in aurum-veil.css — if those durations change,
     this wants revisiting.

     620 originally, which let the glow reach a full swell but made every
     click feel like it hesitated. This is the number to lower first if the
     transition ever feels slow again; the emblem's own 0.44s is the part
     that must not be cut, because clipping that is what looked unfinished. */
  var TAIL_MS = 150;
  var MAX_HOLD_MS = 560;    /* never make someone wait longer than this */
  var REDUCED_HOLD_MS = 160;

  /* TAIL_MS and MAX_HOLD_MS were 300 and 1000. Measured on the live site, a
     typical internal click therefore sat for roughly 740ms before the browser
     was even told to navigate — and only then did the next document start
     loading. That wait IS the "laggy" feeling; nothing about the page was
     slow, we were holding it.

     Two changes together, and the second is what makes the first safe:

       · the hold is roughly halved, leaving the emblem's own 0.44s intact
         (clipping that is what looked unfinished, per the note above)
       · links are now warmed on hover and touch, below, so the destination
         is usually in cache BEFORE the click rather than starting after it

     The net effect is that the shortened hold is spent on an animation whose
     page has already arrived, instead of on an animation followed by a cold
     network fetch. */

  var veil = document.getElementById(VEIL_ID);
  if (!veil) return;

  var timer = null;
  var navigating = false;

  function prefersReduced() {
    return window.matchMedia &&
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function raise() {
    if (veil.classList.contains('is-on')) return;
    veil.classList.add('is-on');

    /* If the navigation never happens — a blocked download, an app link
       that does not resolve, an extension cancelling it — the veil must
       come down on its own. A permanently covered page is a far worse
       failure than a missing transition. */
    clearTimeout(timer);
    timer = setTimeout(lower, SAFETY_MS);
  }

  function lower() {
    clearTimeout(timer);
    navigating = false;
    veil.classList.remove('is-on');
  }

  /* ------------------------------------------------------------------
     Hold the navigation until the veil has actually played.

     Driven off the emblem's own animationend rather than a single hard
     timer, so the wait tracks the CSS instead of duplicating its numbers
     in two files that can drift apart. The timer is still there as a
     backstop, and it is not optional: under prefers-reduced-motion the
     animations are set to none, so animationend never fires at all.
     ------------------------------------------------------------------ */
  function holdThen(go) {
    if (prefersReduced()) { setTimeout(go, REDUCED_HOLD_MS); return; }

    var done = false;
    function fire() {
      if (done) return;
      done = true;
      go();
    }

    var mark = veil.querySelector('.aurum-veil__mark');
    if (mark) {
      mark.addEventListener('animationend', function onEnd(e) {
        if (e.target !== mark) return;          /* ignore the glow and ring */
        mark.removeEventListener('animationend', onEnd);
        setTimeout(fire, TAIL_MS);
      });
    }

    /* Ceiling. Covers a missing element, a browser that never fires the
       event, and the case where the emblem has not decoded yet. */
    setTimeout(fire, MAX_HOLD_MS);
  }

  /* Warm the next document while the veil plays, so the hold is spent on
     network the browser was going to do anyway rather than added to it.
     Best effort — if the response is uncacheable the browser simply
     refetches, and nothing about the transition changes. */
  function prefetch(href) {
    try {
      var l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = href;
      l.as = 'document';
      document.head.appendChild(l);
    } catch (err) { /* not worth breaking a navigation over */ }
  }

  /* ----------------------------------------------------------------------
     Warm links before they are clicked.

     Deliberately NOT the same predicate as the click handler below. That one
     decides whether to take over a navigation and has to be exactly right;
     this one only decides whether to ask the browser to fetch a document
     early, and the cost of a wrong yes is a wasted request.

     So it is written conservatively in the other direction: it refuses
     anything that could have a side effect if fetched. /cart/* mutates the
     cart, /account/logout ends a session, /checkouts/* can consume a token.
     A prefetch is a real GET — treating it as free is how a "performance
     optimisation" quietly empties someone's basket.

     One prefetch per URL per page, capped, because a long catalogue hovered
     end to end would otherwise queue dozens of documents and compete with
     the page the customer is actually reading. */
  var warmed = Object.create(null);
  var warmedCount = 0;
  var WARM_LIMIT = 12;

  function warmable(a) {
    if (!a || warmedCount >= WARM_LIMIT) return null;
    if (a.hasAttribute('download')) return null;
    if (a.target && a.target !== '' && a.target !== '_self') return null;
    if (a.closest('[data-no-veil], cart-remove-button, quantity-input, cart-drawer-items')) return null;

    var href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#') return null;
    if (/^(mailto|tel|sms|javascript):/i.test(href)) return null;

    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return null; }
    if (url.origin !== location.origin) return null;
    if (url.href === location.href) return null;

    /* Anything that acts rather than shows. */
    if (/(?:^|\/)(cart|checkouts|account\/logout|account\/login|discount)(\/|$)/i.test(url.pathname)) return null;

    if (warmed[url.href]) return null;
    return url.href;
  }

  /* pointerover fires for every element the cursor crosses, which on a dense
     catalogue is a steady stream. closest() is cheap but not free, and this
     listener exists to REMOVE jank, so it should not add its own: skip the
     work entirely while the pointer is still inside the element it was in
     last time. */
  var lastTarget = null;

  function warm(e) {
    if (e.target === lastTarget) return;
    lastTarget = e.target;

    var a = e.target && e.target.closest ? e.target.closest('a') : null;
    var href = warmable(a);
    if (!href) return;
    warmed[href] = true;
    warmedCount++;
    prefetch(href);
  }

  /* pointerenter does not bubble, so listen for pointerover on the document
     instead — one listener for the whole page, including links added later
     by the cart drawer or predictive search. */
  document.addEventListener('pointerover', warm, { passive: true });
  /* Touch has no hover. touchstart fires a beat before the click, which is a
     small head start but a real one on a slow connection. */
  document.addEventListener('touchstart', warm, { passive: true });

  function isPlainLeftClick(e) {
    return e.button === 0 &&
           !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey &&
           !e.defaultPrevented;
  }

  document.addEventListener('click', function (e) {
    if (!isPlainLeftClick(e)) return;

    var a = e.target.closest ? e.target.closest('a') : null;
    if (!a) return;

    /* Opt-out for anything that wants to handle its own feedback.

       closest(), not hasAttribute(): the opt-out is nearly always meaningful
       on the component that owns the click, not on the bare <a> that a theme
       happens to render inside it. Dawn's <cart-remove-button> is exactly
       that shape — the anchor is an accessible fallback, the element is what
       actually handles the click. */
    if (a.closest('[data-no-veil]')) return;

    /* Components that handle their own clicks.

       This listener is registered in the CAPTURE phase, which is what made
       this necessary. Capture runs top-down from document, so this fires
       BEFORE any handler bound to the element itself — including the one
       <cart-remove-button> attaches in its constructor. The defaultPrevented
       check above therefore sees `false` no matter how well-behaved the
       component is: it has not had its turn yet.

       The result was that clicking Remove in the cart handed the anchor's
       href to the veil, which faithfully navigated to it. */
    if (a.closest('cart-remove-button, quantity-input, cart-drawer-items')) return;

    /* Opens elsewhere, or saves rather than navigates. */
    if (a.hasAttribute('download')) return;
    if (a.target && a.target !== '' && a.target !== '_self') return;

    var href = a.getAttribute('href');
    if (!href) return;

    /* mailto:, tel:, javascript:, sms: — hand off to the OS, this document
       stays exactly where it is. */
    if (/^(mailto|tel|sms|javascript):/i.test(href)) return;

    /* Bare fragments never load a document. */
    if (href.charAt(0) === '#') return;

    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return; }

    /* Off-site links open a page this script will never see again, and the
       veil would be left hanging over the site behind it. */
    if (url.origin !== location.origin) return;

    /* Cart endpoints are actions, not destinations.

       /cart/change, /cart/clear and friends mutate the cart and bounce
       straight back. Veiling one means playing a page-transition animation
       over what should feel like an in-place edit, and — far worse — it
       means the veil, not the theme's JavaScript, decides how the cart is
       modified.

       That mattered here because the two disagree about how to name a line.
       The theme's AJAX path posts `line: 3`. The anchor's href carries
       `id=<variantId>:<propertiesHash>`, and the GET endpoint will not
       accept a composite key, so it answers "no valid id or line parameter".
       Only lines that carry properties have that suffix, which is why the
       Printful items failed and the coffee — plain, propertyless, so its key
       is a bare variant id the endpoint does accept — appeared to work.

       Matched on the path so it holds for locale-prefixed URLs such as
       /en-us/cart/change, and independently of which element was clicked. */
    if (/(?:^|\/)cart\/(?:change|clear|update|add)\/?$/i.test(url.pathname)) return;

    /* A link to the same path with a different fragment is a scroll, not a
       navigation. This matters here: the home page's story buttons point at
       /pages/our-coffee#..., which IS a real navigation from the home page
       but only a scroll once the customer is already on that page. Comparing
       paths rather than hrefs gets both cases right. */
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;

    /* Same page entirely — Shopify re-renders some links onto themselves. */
    if (url.href === location.href) return;

    /* A second click while the veil is already up: swallow it rather than
       queueing another navigation, but do not start over. */
    if (navigating) { e.preventDefault(); return; }

    /* From here the transition owns the navigation. Everything that could
       disqualify this link has already been checked above — nothing below
       can decide not to navigate, so preventing the default is safe. */
    e.preventDefault();
    navigating = true;

    raise();
    prefetch(url.href);

    holdThen(function () {
      /* assign, not replace: the page being left must stay in history so
         the Back button behaves normally. */
      window.location.assign(url.href);
    });
  }, true);

  /* Coming back via the Back button can restore this page from the bfcache
     with the veil still raised, since the DOM is preserved exactly as it was
     when the customer left. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) lower();
  });

  /* Belt and braces: any return to the tab with the veil up and no
     navigation pending should clear it. */
  window.addEventListener('visibilitychange', function () {
    if (!document.hidden) lower();
  });
})();
